import {
  IClaimResearchRequestResponse,
  IClaimResearchRun,
  IClaimResearchRunListResponse,
  IClaimResearchRunResponse,
  IClaimResearchVerdictBody,
} from "types/claimResearch";

import { api } from ".";

/**
 * CLE-2965 — the SuperAdmin independent claim analysis. SuperAdmin on every endpoint (the API
 * returns 403 otherwise), and nothing here changes the claim: a run is research, kept forever,
 * and a verdict grades the run.
 *
 * The tag is registered here rather than in the shared tagTypes list, so this feature's cache
 * stays in its own file (the Payer Auto-Selection pattern). The tab polls a QUEUED / RUNNING run
 * by id (`useGetClaimResearchRunQuery` with `pollingInterval`); the list is refetched when the
 * polled run settles.
 */
const apiWithClaimResearchTags = api.enhanceEndpoints({
  addTagTypes: ["ClaimResearch"],
});

const listTag = (claimId: string) => ({
  type: "ClaimResearch" as const,
  id: `claim-${claimId}`,
});

export const claimResearchQueries = apiWithClaimResearchTags.injectEndpoints({
  endpoints: (builder) => ({
    /** The claim's runs, newest first, each with its verdicts. */
    getClaimResearchRuns: builder.query<IClaimResearchRun[], string>({
      query: (claimId) =>
        `claims/${encodeURIComponent(claimId)}/independent-analysis`,
      transformResponse: (response: IClaimResearchRunListResponse) =>
        response.data,
      providesTags: (result, _e, claimId) => [
        listTag(claimId),
        ...(result ?? []).map((run) => ({
          type: "ClaimResearch" as const,
          id: run.id,
        })),
      ],
    }),
    /** One run — what the tab polls while it is QUEUED or RUNNING. */
    getClaimResearchRun: builder.query<IClaimResearchRun, string>({
      query: (runId) =>
        `claims/independent-analysis/${encodeURIComponent(runId)}`,
      transformResponse: (response: IClaimResearchRunResponse) => response.data,
      providesTags: (_r, _e, runId) => [{ type: "ClaimResearch", id: runId }],
    }),
    /**
     * Queue a run. 201 `{ created: true }` with a new QUEUED run, 200 `{ created: false }` with the
     * run already in flight for this claim, 503 `CLAIM_RESEARCH_DISABLED` when switched off.
     */
    requestClaimResearch: builder.mutation<
      IClaimResearchRequestResponse,
      string
    >({
      query: (claimId) => ({
        url: `claims/${encodeURIComponent(claimId)}/independent-analysis`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, claimId) => [listTag(claimId)],
    }),
    /** Upsert the current user's verdict on a run. Returns the run with every verdict. */
    saveClaimResearchVerdict: builder.mutation<
      IClaimResearchRun,
      { runId: string; claimId: string; body: IClaimResearchVerdictBody }
    >({
      query: ({ runId, body }) => ({
        url: `claims/independent-analysis/${encodeURIComponent(runId)}/verdict`,
        method: "PUT",
        body,
      }),
      transformResponse: (response: IClaimResearchRunResponse) => response.data,
      invalidatesTags: (_r, _e, { runId, claimId }) => [
        { type: "ClaimResearch", id: runId },
        listTag(claimId),
      ],
    }),
  }),
});

export const {
  useGetClaimResearchRunsQuery,
  useGetClaimResearchRunQuery,
  useRequestClaimResearchMutation,
  useSaveClaimResearchVerdictMutation,
} = claimResearchQueries;
